/**
 * Rivian VIN Decoder
 *
 * Decodes Rivian vehicle VINs by extracting model (position 4, 0-based index 3)
 * and model year (position 10, 0-based index 9) from a standard 17-character VIN.
 *
 * Exposed on `window.RivianVinDecoder` with three pure helpers:
 *   - decodeVin(vin)                         -> { ok, value } | { ok, error }
 *   - decodeVins(vins)                       -> { successes, failures }
 *   - filterDecodedVins(decoded, criteria)   -> filtered array
 *
 * Only Rivian-specific model codes and MY2021–MY2030 year codes are supported.
 * Invalid input returns structured error objects — no exceptions are thrown.
 */
;(function () {
  const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/

  /** Position 4 (0-based index 3) — Rivian model codes */
  const MODEL_TABLE = Object.freeze({
    T: Object.freeze({ code: "T", modelKey: "R1T", label: "R1T" }),
    S: Object.freeze({ code: "S", modelKey: "R1S", label: "R1S" }),
    E: Object.freeze({ code: "E", modelKey: "EDV", label: "EDV" }),
    C: Object.freeze({ code: "C", modelKey: "RCV", label: "RCV" }),
    2: Object.freeze({ code: "2", modelKey: "R2", label: "R2" }),
  })

  /** Position 10 (0-based index 9) — CFR Part 565 year codes, MY2021–MY2030 */
  const YEAR_TABLE = Object.freeze({
    M: 2021,
    N: 2022,
    P: 2023,
    R: 2024,
    S: 2025,
    T: 2026,
    V: 2027,
    W: 2028,
    X: 2029,
    Y: 2030,
  })

  const makeError = (rawVin, code, message) => ({
    ok: false,
    error: Object.freeze({ rawVin, code, message }),
  })
  /**
   * Decode a single Rivian VIN, extracting model and model year.
   * @param {string} vin
   * @returns {{ ok: true, value: { rawVin: string, model: object, year: object } }
   *         | { ok: false, error: { rawVin: string, code: string, message: string } }}
   */
  const decodeVin = (vin) => {
    const normalized = (vin || "").trim().toUpperCase()

    if (normalized.length !== 17) {
      return makeError(
        normalized,
        "INVALID_FORMAT",
        `VIN must be exactly 17 characters (got ${normalized.length}).`
      )
    }

    if (!VIN_REGEX.test(normalized)) {
      return makeError(
        normalized,
        "INVALID_FORMAT",
        "VIN contains invalid characters. Only A-Z (excluding I, O, Q) and 0-9 are allowed."
      )
    }

    const modelChar = normalized[3]
    const modelInfo = MODEL_TABLE[modelChar]

    if (!modelInfo) {
      return makeError(
        normalized,
        "UNKNOWN_MODEL_CODE",
        `Unrecognized Rivian model code "${modelChar}" at VIN position 4.`
      )
    }

    const yearChar = normalized[9]
    const modelYear = YEAR_TABLE[yearChar]

    if (modelYear === undefined) {
      return makeError(
        normalized,
        "UNKNOWN_YEAR_CODE",
        `Unrecognized model year code "${yearChar}" at VIN position 10.`
      )
    }

    return {
      ok: true,
      value: Object.freeze({
        rawVin: normalized,
        model: modelInfo,
        year: Object.freeze({ yearCode: yearChar, modelYear }),
      }),
    }
  }

  /**
   * Decode a batch of VINs, partitioning successes and failures.
   * @param {string[]} vins
   * @returns {{ successes: object[], failures: object[] }}
   */
  const decodeVins = (vins) => {
    const successes = []
    const failures = []

    for (const vin of vins) {
      const result = decodeVin(vin)

      if (result.ok) {
        successes.push(result.value)
      } else {
        failures.push(result.error)
      }
    }

    return { successes, failures }
  }
  /**
   * Filter decoded VIN results by optional model keys and/or model years.
   * Omitted criteria are not applied.
   * @param {object[]} decoded - Array of VinDecodeResult objects
   * @param {{ modelKeys?: string[], modelYears?: number[] }} criteria
   * @returns {object[]}
   */
  const filterDecodedVins = (decoded, criteria) => {
    const { modelKeys, modelYears } = criteria || {}

    const hasModelFilter = Array.isArray(modelKeys) && modelKeys.length > 0
    const hasYearFilter = Array.isArray(modelYears) && modelYears.length > 0

    if (!hasModelFilter && !hasYearFilter) {
      return decoded
    }

    const modelKeySet = hasModelFilter ? new Set(modelKeys) : null
    const modelYearSet = hasYearFilter ? new Set(modelYears) : null

    return decoded.filter((entry) => {
      if (modelKeySet && !modelKeySet.has(entry.model.modelKey)) {
        return false
      }

      if (modelYearSet && !modelYearSet.has(entry.year.modelYear)) {
        return false
      }

      return true
    })
  }
  window.RivianVinDecoder = Object.freeze({
    decodeVin,
    decodeVins,
    filterDecodedVins,
  })
})()
